import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import api from "../api"; // axios instance with token + school_id interceptor

const SubjectPage = () => {
  const [form, setForm] = useState({ subject_name: "", code: "" });
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [msg, setMsg] = useState(null);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // API base path (interceptor already prepends /api)
  const baseURL = "/subjects";
  const schoolId = localStorage.getItem("school_id");

  // fetch subjects list
  const fetchSubjects = async () => {
    try {
      const res = await api.get(`${baseURL}/list?school_id=${schoolId}`);
      const data = res.data;
      const list = data?.data ?? [];
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching subjects:", err);
      setItems([]);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);

    if (!form.subject_name || form.subject_name.trim() === "") {
      setMsg({ type: "error", text: "Subject name required" });
      return;
    }

    const payload = { ...form };

    try {
      let res;
      if (editId) {
        res = await api.post(`${baseURL}/update`, { ...payload, id: editId });
      } else {
        res = await api.post(`${baseURL}/add`, payload);
      }

      const json = res.data;

      if (json?.status) {
        setMsg({
          type: "success",
          text: json.message || (editId ? "Updated successfully" : "Added successfully"),
        });
      } else {
        setMsg({ type: "error", text: json?.message || "Operation failed" });
      }

      setForm({ subject_name: "", code: "" });
      setEditId(null);
      fetchSubjects();
    } catch (err) {
      console.error("Error saving subject:", err);
      setMsg({ type: "error", text: err.response?.data?.message || err.message || "Network error" });
    }
  };

  const handleEdit = (item) => {
    setForm({ subject_name: item.subject_name ?? "", code: item.code ?? "" });
    setEditId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subject?")) return;
    try {
      const res = await api.post(`${baseURL}/delete`, { id });
      const json = res.data;
      if (json?.status) {
        setMsg({ type: "success", text: json.message || "Deleted" });
      } else {
        setMsg({ type: "error", text: json?.message || "Delete failed" });
      }
      fetchSubjects();
    } catch (err) {
      console.error("Delete error", err);
      setMsg({ type: "error", text: err.response?.data?.message || err.message || "Network error" });
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <div style={{ flex: 1, backgroundColor: "#f4f6f8" }}>
        <main
          style={{
            padding: "30px",
            maxWidth: "1000px",
            margin: "auto",
            marginTop: "0px",
          }}
        >
          {/* Add/Edit Subject Section */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>{editId ? "✏️ Edit Subject" : "➕ Add New Subject"}</h2>

            {msg && (
              <div style={{ marginBottom: 12, color: msg.type === "error" ? "#cc0000" : "#166534" }}>
                {msg.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={formGrid}>
                <FormGroup label="Subject Name">
                  <input
                    type="text"
                    name="subject_name"
                    value={form.subject_name}
                    onChange={handleChange}
                    style={fullWidthInput}
                    required
                  />
                </FormGroup>

                <FormGroup label="Code (optional)">
                  <input
                    type="text"
                    name="code"
                    value={form.code}
                    onChange={handleChange}
                    style={fullWidthInput}
                  />
                </FormGroup>
              </div>

              <div style={{ textAlign: "right", marginTop: "20px" }}>
                <button type="submit" style={buttonStyle}>
                  {editId ? "Update Subject" : "Add Subject"}
                </button>
              </div>
            </form>
          </section>

          {/* List Subjects */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>📋 All Subjects</h2>
            {items.length === 0 ? (
              <p>No subjects found.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {items.map((item, idx) => (
                  <li
                    key={item.id}
                    style={{
                      marginBottom: "15px",
                      padding: "15px",
                      borderRadius: "8px",
                      background: idx % 2 === 0 ? "#f9f9f9" : "#eef3f9",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: "16px", color: "#333" }}>{item.subject_name}</strong>
                      <div style={{ color: "#777", fontSize: "13px" }}>
                        Code: {item.code ?? "-"} {item.created_at ? ` | Created: ${item.created_at}` : ""}
                      </div>
                    </div>
                    <div>
                      <FaEdit
                        onClick={() => handleEdit(item)}
                        style={{ color: "#0077cc", cursor: "pointer", marginRight: "15px" }}
                      />
                      <FaTrash onClick={() => handleDelete(item.id)} style={{ color: "red", cursor: "pointer" }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

// Reusable Form Group
const formGrid = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "20px",
};

const fullWidthInput = {
  width: "100%",
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: "15px",
  boxSizing: "border-box",
};

const FormGroup = ({ label, children, style }) => (
  <div style={{ ...style }}>
    <label style={{ marginBottom: "6px", display: "block", fontWeight: 500, color: "#444" }}>{label}</label>
    {children}
  </div>
);

const buttonStyle = {
  padding: "10px 25px",
  backgroundColor: "#0077cc",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontSize: "15px",
  cursor: "pointer",
};

const cardStyle = {
  background: "#ffffff",
  padding: "25px",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  marginBottom: "30px",
};

const titleStyle = {
  color: "#0077cc",
  marginBottom: "25px",
};

export default SubjectPage;
